import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNotification } from '../context/NotificationContext';
import { normalizeMobileNumber, formatMobileForDisplay, formatNumberPersian } from '../utils/mobileUtils';
import { formatPrice } from '../utils/priceUtils';
import { apiFetch } from '../utils/api';
import { userFacingMessageFromResponse } from '../utils/userMessages';
import './Checkout.css';

function Checkout() {
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [address, setAddress] = useState({
    province: '',
    city: '',
    postalCode: '',
    fullAddress: '',
    plaque: '',
    unit: '',
    receiverName: user?.name || '',
    receiverMobile: user?.mobile ? formatMobileForDisplay(user.mobile) : ''
  });

  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteAddressId, setDeleteAddressId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newAddress, setNewAddress] = useState({
    province: '',
    city: '',
    postalCode: '',
    fullAddress: '',
    plaque: '',
    unit: '',
    receiverName: user?.name || '',
    receiverMobile: user?.mobile ? formatMobileForDisplay(user.mobile) : ''
  });

  useEffect(() => {
    if (user?.mobile) {
      fetchAddresses();
    } else {
      setLoadingAddresses(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.mobile]);

  const fetchAddresses = async () => {
    try {
      const response = await apiFetch(`/api/addresses/user/${user.mobile}`);
      if (response.ok) {
        const data = await response.json();
        setAddresses(data);
        if (data.length > 0) {
          const firstAddress = data[0];
          setSelectedAddress(firstAddress);
          setAddress({
            province: firstAddress.province || '',
            city: firstAddress.city || '',
            postalCode: firstAddress.postalCode || '',
            fullAddress: firstAddress.fullAddress || '',
            plaque: firstAddress.plaque || '',
            unit: firstAddress.unit || '',
            receiverName: firstAddress.receiverName || user?.name || '',
            receiverMobile: firstAddress.receiverMobile ? formatMobileForDisplay(firstAddress.receiverMobile) : (user?.mobile ? formatMobileForDisplay(user.mobile) : '')
          });
        }
      }
    } catch (error) {
      // Ignore error
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleDeleteAddress = (addressId, e) => {
    e.stopPropagation();
    setDeleteAddressId(addressId);
    setShowDeleteDialog(true);
  };

  const handleEditAddress = (address, e) => {
    e.stopPropagation();
    setEditingAddress(address);
    setNewAddress({
      province: address.province || '',
      city: address.city || '',
      postalCode: address.postalCode || '',
      fullAddress: address.fullAddress || '',
      plaque: address.plaque || '',
      unit: address.unit || '',
      receiverName: address.receiverName || user?.name || '',
      receiverMobile: address.receiverMobile ? formatMobileForDisplay(address.receiverMobile) : (user?.mobile ? formatMobileForDisplay(user.mobile) : '')
    });
    setShowAddressForm(true);
  };

  const confirmDeleteAddress = async () => {
    if (!deleteAddressId) return;
    try {
      const response = await apiFetch(`/api/addresses/${deleteAddressId}`, { method: 'DELETE' });
      if (response.ok) {
        await fetchAddresses();
        if (selectedAddress?.id === deleteAddressId) {
          setSelectedAddress(null);
          setAddress({
            province: '', city: '', postalCode: '', fullAddress: '', plaque: '', unit: '',
            receiverName: user?.name || '',
            receiverMobile: user?.mobile ? formatMobileForDisplay(user.mobile) : ''
          });
        }
        showNotification('آدرس با موفقیت حذف شد', 'success');
      } else {
        showNotification('خطا در حذف آدرس', 'error');
      }
    } catch (error) {
      showNotification('خطا در حذف آدرس', 'error');
    } finally {
      setShowDeleteDialog(false);
      setDeleteAddressId(null);
    }
  };

  const cancelDeleteAddress = () => {
    setShowDeleteDialog(false);
    setDeleteAddressId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (addresses.length > 0 && !selectedAddress) {
      showNotification('لطفاً یک آدرس را انتخاب کنید', 'error');
      return;
    }
    if (!address.province || !address.city || !address.postalCode || !address.fullAddress || !address.receiverName || !address.receiverMobile) {
      showNotification('لطفاً تمام فیلدها را پر کنید', 'error');
      return;
    }
    const normalizedReceiverMobile = normalizeMobileNumber(address.receiverMobile);
    if (!normalizedReceiverMobile) {
      showNotification('شماره موبایل گیرنده نامعتبر است', 'error');
      return;
    }
    setLoading(true);
    try {
      const orderData = {
        mobile: user.mobile,
        items: cartItems.map(item => ({ productId: item.productId, quantity: item.quantity })),
        address: { ...address, receiverMobile: normalizedReceiverMobile },
        discountCode: appliedDiscount ? appliedDiscount.code : (discountCode || null)
      };
      const response = await apiFetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });
      if (response.ok) {
        const order = await response.json();
        clearCart();
        showNotification('سفارش با موفقیت ثبت شد', 'success');
        navigate(`/payment/${order.id}`);
      } else {
        const msg = await userFacingMessageFromResponse(response, 'خطا در ثبت سفارش');
        console.error('Order creation failed:', msg);
        showNotification(msg, 'error');
      }
    } catch (error) {
      showNotification('خطا در ثبت سفارش', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscount = (code, total) => {
    if (!code || code.trim() === '') return 0;
    const codeUpper = code.toUpperCase();
    if (codeUpper === 'DISCOUNT10') return total * 0.1;
    if (codeUpper === 'DISCOUNT20') return total * 0.2;
    return 0;
  };

  const handleApplyDiscount = () => {
    if (!discountCode || discountCode.trim() === '') {
      showNotification('لطفاً کد تخفیف را وارد کنید', 'error');
      return;
    }
    setDiscountLoading(true);
    const total = getTotalPrice();
    const discount = calculateDiscount(discountCode, total);
    if (discount > 0) {
      setAppliedDiscount({ code: discountCode, amount: discount });
      showNotification('کد تخفیف با موفقیت اعمال شد', 'success');
    } else {
      setAppliedDiscount(null);
      showNotification('کد تخفیف نامعتبر است', 'error');
    }
    setDiscountLoading(false);
  };

  const getFinalPrice = () => {
    const total = getTotalPrice();
    const discount = appliedDiscount ? appliedDiscount.amount : 0;
    return Math.max(0, total - discount);
  };

  const resetNewAddress = () => ({
    province: '', city: '', postalCode: '', fullAddress: '', plaque: '', unit: '',
    receiverName: user?.name || '',
    receiverMobile: user?.mobile ? formatMobileForDisplay(user.mobile) : ''
  });

  return (
    <div className={`checkout-container ${isDarkMode ? 'dark' : ''}`}>
      <div className="checkout-header">
        <h1>تکمیل اطلاعات و ثبت سفارش</h1>
        <button className="back-btn" onClick={() => navigate('/cart')}>→ بازگشت به سبد خرید</button>
      </div>
      <div className="checkout-content">
        <form onSubmit={handleSubmit} className="checkout-form">
          <div className="form-section">
            <h2>اطلاعات ارسال</h2>
            {loadingAddresses ? (
              <div className="loading">در حال بارگذاری آدرس‌ها...</div>
            ) : addresses.length > 0 ? (
              <div className="address-selection">
                <h3>انتخاب آدرس ارسال</h3>
                <div className="addresses-grid">
                  {addresses.map(addr => (
                    <div
                      key={addr.id}
                      className={`address-card-selectable ${selectedAddress?.id === addr.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedAddress(addr);
                        setAddress({
                          province: addr.province || '',
                          city: addr.city || '',
                          postalCode: addr.postalCode || '',
                          fullAddress: addr.fullAddress || '',
                          plaque: addr.plaque || '',
                          unit: addr.unit || '',
                          receiverName: addr.receiverName || user?.name || '',
                          receiverMobile: addr.receiverMobile ? formatMobileForDisplay(addr.receiverMobile) : (user?.mobile ? formatMobileForDisplay(user.mobile) : '')
                        });
                      }}
                    >
                      <div className="address-card-actions">
                        <button type="button" className="edit-address-btn" onClick={(e) => handleEditAddress(addr, e)} title="ویرایش آدرس">✎</button>
                        <button type="button" className="delete-address-btn" onClick={(e) => handleDeleteAddress(addr.id, e)} title="حذف آدرس">×</button>
                      </div>
                      <div className="address-card-header"><h4>{addr.receiverName}</h4></div>
                      <div className="address-card-body">
                        <p><strong>استان:</strong> {addr.province}</p>
                        <p><strong>شهر:</strong> {addr.city}</p>
                        <p><strong>آدرس:</strong> {addr.fullAddress}</p>
                        {addr.plaque && <p><strong>پلاک:</strong> {addr.plaque}</p>}
                        {addr.unit && <p><strong>واحد:</strong> {addr.unit}</p>}
                        <p><strong>کد پستی:</strong> {formatNumberPersian(addr.postalCode)}</p>
                        <p><strong>شماره تماس:</strong> {formatMobileForDisplay(addr.receiverMobile)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" className="btn-add-address" onClick={() => setShowAddressForm(true)}>+ افزودن آدرس جدید</button>
              </div>
            ) : (
              <div className="no-address-section">
                <p>شما هنوز آدرسی ثبت نکرده‌اید.</p>
                <button type="button" className="btn-add-address" onClick={() => setShowAddressForm(true)}>+ افزودن آدرس جدید</button>
              </div>
            )}
          </div>
          <div className="form-section">
            <h2>کد تخفیف</h2>
            <div className="discount-input-group">
              <input type="text" value={discountCode} onChange={(e) => { setDiscountCode(e.target.value); setAppliedDiscount(null); }} placeholder="کد تخفیف خود را وارد کنید" className="discount-input" />
              <button type="button" className="btn-apply-discount" onClick={handleApplyDiscount} disabled={discountLoading || !discountCode.trim()}>
                {discountLoading ? 'در حال اعمال...' : 'اعمال کد تخفیف'}
              </button>
            </div>
            {appliedDiscount && (
              <div className="discount-applied">
                <span>✓ کد تخفیف {appliedDiscount.code} اعمال شد</span>
                <button type="button" className="btn-remove-discount" onClick={() => { setAppliedDiscount(null); setDiscountCode(''); }}>حذف</button>
              </div>
            )}
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>{loading ? 'در حال ثبت سفارش...' : 'ثبت و پرداخت سفارش'}</button>
        </form>
        <div className="order-summary">
          <h2>خلاصه سفارش</h2>
          <div className="summary-items">
            {cartItems.map(item => (
              <div key={item.productId} className="summary-item">
                <span>{item.product.name} × {formatNumberPersian(item.quantity)}</span>
                <span>{formatPrice(item.product.price * item.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="summary-total"><span>جمع کل:</span><span>{formatPrice(getTotalPrice())}</span></div>
          {appliedDiscount && (
            <div className="summary-discount">
              <span>تخفیف ({appliedDiscount.code}):</span>
              <span className="discount-amount">-{formatPrice(appliedDiscount.amount)}</span>
            </div>
          )}
          <div className="summary-total"><span>مبلغ قابل پرداخت:</span><span className="final-price">{formatPrice(getFinalPrice())}</span></div>
        </div>
      </div>

      {showAddressForm && (
        <div className="address-form-overlay" onClick={() => { setShowAddressForm(false); setEditingAddress(null); setNewAddress(resetNewAddress()); }}>
          <div className="address-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="address-form-header">
              <h3>{editingAddress ? 'ویرایش آدرس' : 'افزودن آدرس جدید'}</h3>
              <button className="close-btn" onClick={() => { setShowAddressForm(false); setEditingAddress(null); setNewAddress(resetNewAddress()); }}>×</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const normalizedReceiverMobile = normalizeMobileNumber(newAddress.receiverMobile);
              if (!normalizedReceiverMobile) {
                showNotification('شماره موبایل گیرنده نامعتبر است', 'error');
                return;
              }
              try {
                const addressData = { ...newAddress, receiverMobile: normalizedReceiverMobile, mobile: user.mobile };
                let response;
                if (editingAddress) {
                  response = await apiFetch(`/api/addresses/${editingAddress.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(addressData)
                  });
                } else {
                  response = await apiFetch('/api/addresses', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(addressData)
                  });
                }
                if (response.ok) {
                  await fetchAddresses();
                  setShowAddressForm(false);
                  setEditingAddress(null);
                  setNewAddress(resetNewAddress());
                  showNotification(editingAddress ? 'آدرس با موفقیت به‌روزرسانی شد' : 'آدرس با موفقیت اضافه شد', 'success');
                } else if (response.status === 409) {
                  showNotification('این آدرس قبلاً ثبت شده است', 'error');
                } else {
                  showNotification(editingAddress ? 'خطا در به‌روزرسانی آدرس' : 'خطا در اضافه کردن آدرس', 'error');
                }
              } catch (err) {
                showNotification(editingAddress ? 'خطا در به‌روزرسانی آدرس' : 'خطا در اضافه کردن آدرس', 'error');
              }
            }}>
              <div className="form-row">
                <div className="form-group"><label>استان</label><input type="text" value={newAddress.province} onChange={(e) => setNewAddress({...newAddress, province: e.target.value})} required /></div>
                <div className="form-group"><label>شهر</label><input type="text" value={newAddress.city} onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} required /></div>
              </div>
              <div className="form-group">
                <label>کد پستی</label>
                <input type="text" value={newAddress.postalCode} onChange={(e) => setNewAddress({...newAddress, postalCode: e.target.value.replace(/[^0-9۰-۹]/g, '')})} placeholder="۱۲۳۴۵۶۷۸۹۰" maxLength="10" required />
              </div>
              <div className="form-group"><label>آدرس کامل</label><textarea value={newAddress.fullAddress} onChange={(e) => setNewAddress({...newAddress, fullAddress: e.target.value})} rows="4" required /></div>
              <div className="form-row">
                <div className="form-group"><label>پلاک</label><input type="text" value={newAddress.plaque} onChange={(e) => setNewAddress({...newAddress, plaque: e.target.value})} /></div>
                <div className="form-group"><label>واحد</label><input type="text" value={newAddress.unit} onChange={(e) => setNewAddress({...newAddress, unit: e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>نام گیرنده</label><input type="text" value={newAddress.receiverName} onChange={(e) => setNewAddress({...newAddress, receiverName: e.target.value})} required /></div>
                <div className="form-group">
                  <label>شماره موبایل گیرنده</label>
                  <input type="tel" value={newAddress.receiverMobile} onChange={(e) => setNewAddress({...newAddress, receiverMobile: e.target.value.replace(/[^0-9۰-۹]/g, '')})} placeholder="۰۹۱۲۳۴۵۶۷۸۹" maxLength="11" required />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">{editingAddress ? 'ذخیره تغییرات' : 'افزودن آدرس'}</button>
                <button type="button" className="btn-cancel" onClick={() => { setShowAddressForm(false); setEditingAddress(null); setNewAddress(resetNewAddress()); }}>انصراف</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteDialog && (
        <div className="delete-dialog-overlay" onClick={cancelDeleteAddress}>
          <div className="delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="delete-dialog-header"><h3>حذف آدرس</h3></div>
            <div className="delete-dialog-content"><p>آیا قصد حذف این آدرس را دارید؟</p></div>
            <div className="delete-dialog-actions">
              <button type="button" className="btn-delete-confirm" onClick={confirmDeleteAddress}>بله</button>
              <button type="button" className="btn-delete-cancel" onClick={cancelDeleteAddress}>خیر</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Checkout;
